import React from "react";
import PageShell from "../components/layout/PageShell";
import CinematicHero, { HeroOverlay } from "../components/layout/CinematicHero";
import Container from "../components/primitives/Container";
import Section from "../components/primitives/Section";
import Eyebrow from "../components/primitives/Eyebrow";
import Reveal from "../components/primitives/Reveal";
import ContactForm from "../components/editorial/ContactForm";
import SeoHead from "../components/seo/SeoHead";
import { IMAGES } from "../content/images";

const Contact = () => {
  return (
    <PageShell>
      <SeoHead
        title="Contact"
        description="The Office of the Chairman serves as the principal point of contact for strategic partnerships, institutional investors, and confidential corporate matters."
        path="/contact"
        ogImage={IMAGES.contact_stone_entrance}
      />
      <CinematicHero
        image={IMAGES.contact_stone_entrance}
        imageAlt="An empty executive office interior in early light."
        eager
        data-testid="contact-hero"
      >
        <HeroOverlay
          eyebrow="Contact"
          title={["Correspondence.", "By design, not by form."]}
          statement="The Office of the Chairman is the principal point of contact."
        />
      </CinematicHero>

      {/* Section 1 — Office of the Chairman (elevated typography — no anchor image) */}
      <Section tone="light" className="pt-20 md:pt-24 lg:pt-28 pb-8 md:pb-10 lg:pb-10" data-testid="contact-chairman">
        <Container>
          <Reveal className="max-w-3xl mx-auto text-center flex flex-col items-center">
            <span aria-hidden="true" className="block h-px bg-gold" style={{ width: 48 }} />
            <div className="mt-10">
              <Eyebrow tone="gold">Office of the Chairman</Eyebrow>
            </div>
            <h2
              className="mt-10 font-serif font-normal text-navy text-balance"
              style={{ fontSize: "clamp(48px, 6.5vw, 88px)", lineHeight: 1.06, letterSpacing: "-0.015em" }}
            >
              Office of the Chairman
            </h2>
            <p className="mt-12 font-sans text-[16px] sm:text-[17px] leading-[1.75] text-navy/80 max-w-[60ch]">
              The Office of the Chairman serves as the principal point of contact for matters relating to strategic partnerships, institutional investors, family offices, sovereign wealth funds, and confidential corporate matters.
            </p>
            <p className="mt-10 font-serif italic text-navy/60" style={{ fontSize: "17px" }}>
              For correspondence intended for the Office of the Chairman, please contact:
            </p>
            <a
              href="mailto:chairman@quscap.com"
              data-testid="chairman-email"
              className="group mt-8 inline-block font-serif text-navy"
              style={{ fontSize: "clamp(22px, 2.2vw, 32px)" }}
            >
              <span className="relative">
                chairman@quscap.com
                <span
                  aria-hidden="true"
                  className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left transition-transform duration-300 ease-out group-hover:scale-x-100"
                />
              </span>
            </a>
            <span aria-hidden="true" className="mt-10 block h-px w-8 bg-gold" />
          </Reveal>
        </Container>
      </Section>

      {/* Divider */}
      <div className="bg-offwhite">
        <Container>
          <div className="flex flex-col items-center py-6 md:py-8">
            <span
              className="font-sans uppercase text-navy/55"
              style={{ fontSize: "11px", letterSpacing: "0.22em" }}
            >
              General Inquiries
            </span>
            <span aria-hidden="true" className="mt-6 block w-full h-px bg-navy/8" />
          </div>
        </Container>
      </div>

      {/* Section 2 — General Inquiries form */}
      <Section tone="offwhite" className="pt-8 md:pt-10 lg:pt-12 pb-24 md:pb-32 lg:pb-40" data-testid="contact-general">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
            <Reveal className="lg:col-span-5">
              <Eyebrow tone="gold">General Inquiries</Eyebrow>
              <h2
                className="mt-8 font-serif font-normal text-navy text-balance"
                style={{ fontSize: "clamp(28px, 3vw, 44px)", lineHeight: 1.15, letterSpacing: "-0.005em" }}
              >
                For business inquiries and general correspondence.
              </h2>
              <p className="mt-8 font-sans text-[16px] leading-[1.7] text-navy/80 max-w-[42ch]">
                Submissions are reviewed by the firm's office in due course. The form is intended for business inquiries and general correspondence — not for strategic or institutional matters.
              </p>
              <p className="mt-8 font-serif italic text-navy/60" style={{ fontSize: "17px" }}>
                Business inquiries only.
              </p>
            </Reveal>
            <Reveal className="lg:col-span-7" delay={120}>
              <ContactForm />
            </Reveal>
          </div>
        </Container>
      </Section>

      {/* Closing */}
      <Section tone="light" className="py-24 md:py-32" data-testid="contact-closing">
        <Container>
          <Reveal className="mx-auto max-w-lg flex flex-col items-center text-center gap-6">
            <p className="font-serif text-2xl sm:text-3xl text-navy">Quantum US Capital Partners LLC</p>
            <span
              className="font-sans uppercase text-gold"
              style={{ fontSize: "11px", letterSpacing: "0.22em" }}
            >
              The Art of Capital
            </span>
            <span aria-hidden="true" className="block h-px w-8 bg-gold" />
          </Reveal>
        </Container>
      </Section>
    </PageShell>
  );
};

export default Contact;
